import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useCollectionBinding } from '@vibesites/runtime/react';
import { usePropertyField, useProperty, RichTextContent, useSlotGroup, EditableSlotGroup } from '@vibesites/runtime/react';

// CTA shown beneath the "Everything Is Within Reach" heading, above the map.
const ctaDef: SlotGroupDefinition = {
  label: 'CTA',
  fields: {
    url: { label: 'Button URL', type: 'url', defaultValue: '/schedule-a-tour' },
    label: { label: 'Button Label', type: 'text', defaultValue: 'Schedule a Tour' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

interface POI {
  poi_name?: string;
  poi_type?: string;
  poi_address?: string;
  poi_short_description?: string;
  poi_long_description?: string;
  poi_featured_image?: string;
  poi_business_url?: string;
  poi_business_phone?: string;
  poi_directions_map_url?: string;
  poi_miles_from_property?: string;
  poi_walking_minutes?: string;
  poi_driving_minutes?: string;
  poi_biking_minutes?: string;
  poi_latitude?: string;
  poi_longitude?: string;
  poi_map_icon?: string;
  poi_google_place_id?: string;
  poi_place_primary_type?: string;
}

function hasVal(v: string | null | undefined): v is string {
  return !!v && v !== 'false' && v !== 'null' && v !== 'undefined' && v.trim() !== '';
}

// Stable identity for a place, used to collapse the same POI when it's listed
// under more than one category (e.g. a hospital tagged both "Health" and
// "Nearby"). Prefer the Google place ID; otherwise fall back to a normalized
// name + address so entries typed slightly differently still match.
function poiIdentity(poi: POI): string {
  if (hasVal(poi.poi_google_place_id)) return `id:${poi.poi_google_place_id.trim()}`;
  const name = (poi.poi_name || '').trim().toLowerCase();
  const addr = (poi.poi_address || '').trim().toLowerCase();
  return `na:${name}|${addr}`;
}

// ============================================================================
// Map styling & markers
// ============================================================================

const MAPS_API_KEY =
  (typeof import.meta !== 'undefined' && (import.meta as any).env?.GOOGLE_MAPS_API_KEY) || '';

let mapsLoaderPromise: Promise<void> | null = null;

function loadGoogleMaps(): Promise<void> {
  if ((window as any).google?.maps?.Map) return Promise.resolve();
  if (mapsLoaderPromise) return mapsLoaderPromise;
  mapsLoaderPromise = new Promise<void>((resolve, reject) => {
    if (!MAPS_API_KEY) { reject(new Error('No key')); return; }
    const cb = `__gmapsInit_${Date.now()}`;
    (window as any)[cb] = () => { delete (window as any)[cb]; resolve(); };
    const s = document.createElement('script');
    s.src = `https://maps.googleapis.com/maps/api/js?key=${MAPS_API_KEY}&callback=${cb}`;
    s.onerror = () => { mapsLoaderPromise = null; reject(new Error('Script failed')); };
    document.head.appendChild(s);
  });
  return mapsLoaderPromise;
}

function makeSvgMarker(fillColor: string, glyphSvg: string, size = 30): string {
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${Math.round(size * 1.3)}" viewBox="0 0 30 39">
  <path d="M15 0C6.72 0 0 6.72 0 15c0 11.25 15 24 15 24s15-12.75 15-24C30 6.72 23.28 0 15 0z" fill="${fillColor}" stroke="#fff" stroke-width="1.5"/>
  <g transform="translate(7,7)" fill="#fff"><g transform="translate(8,8) scale(0.55) translate(-8,-8)">${glyphSvg}</g></g>
</svg>`)}`;
}

function makeHighlightMarker(fillColor: string, glyphSvg: string, size = 40): string {
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${Math.round(size * 1.35)}" viewBox="0 0 40 54">
  <ellipse cx="20" cy="51" rx="8" ry="3" fill="rgba(0,0,0,0.2)"/>
  <path d="M20 0C9 0 0 9 0 20c0 15 20 31 20 31s20-16 20-31C40 9 31 0 20 0z" fill="${fillColor}" stroke="#fff" stroke-width="2"/>
  <g transform="translate(10,10)" fill="#fff"><g transform="translate(10,10) scale(0.7) translate(-8,-8)">${glyphSvg}</g></g>
</svg>`)}`;
}

function makePropertyPinBg(primaryColor: string, hasIcon: boolean): string {
  const inner = hasIcon
    ? `<circle cx="30" cy="27" r="18" fill="#fff"/>`
    : `<circle cx="30" cy="27" r="18" fill="#fff" opacity="0.95"/>
       <path d="M24 26v10h4v-5h4v5h4V26l-6-5z" fill="${primaryColor}"/>
       <path d="M21 27l9-8 9 8" fill="none" stroke="${primaryColor}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" width="60" height="74" viewBox="0 0 60 74">
  <ellipse cx="30" cy="70" rx="12" ry="4" fill="rgba(0,0,0,0.2)"/>
  <path d="M30 0C13.43 0 0 13.43 0 30c0 22.5 30 42 30 42s30-19.5 30-42C60 13.43 46.57 0 30 0z" fill="${primaryColor}" stroke="#fff" stroke-width="2.5"/>
  ${inner}
</svg>`)}`;
}

const CATEGORY_ICONS: Record<string, { glyph: string; hue: number }> = {
  restaurant: { glyph: '<path d="M2 0v6h2v10h2V6h2V0H6V4H4V0zm8 0v8h4V6h2v10h2V0z" transform="scale(0.9)"/>', hue: 0 },
  shopping: { glyph: '<path d="M2 2v2H0v10h16V4h-2V2a4 4 0 00-8 0v0zm2 0a2 2 0 014 0v2H4V2z" transform="scale(0.95)"/>', hue: 270 },
  education: { glyph: '<path d="M8 0L0 4l8 4 8-4-8-4zM2 6v4l6 3 6-3V6L8 9 2 6z"/>', hue: 200 },
  health: { glyph: '<path d="M6 0v6H0v4h6v6h4v-6h6V6h-6V0z" transform="scale(0.95)"/>', hue: 350 },
  fitness: { glyph: '<path d="M6 0v6H0v4h6v6h4v-6h6V6h-6V0z" transform="scale(0.95)"/>', hue: 350 },
  entertainment: { glyph: '<path d="M8 0a8 8 0 100 16A8 8 0 008 0zm0 2a6 6 0 110 12A6 6 0 018 2zm-2 3v6l5-3-5-3z" transform="scale(0.95)"/>', hue: 45 },
  park: { glyph: '<path d="M8 0L4 6h2L2 12h12L10 6h2L8 0zM7 12v4h2v-4z" transform="scale(0.95)"/>', hue: 140 },
  transit: { glyph: '<path d="M4 0C2 0 0 1 0 3v7a3 3 0 003 3l-1 2v1h2l1-2h6l1 2h2v-1l-1-2a3 3 0 003-3V3c0-2-2-3-4-3zm0 2h8v4H4V2zm0 7a1.5 1.5 0 110 3 1.5 1.5 0 010-3zm8 0a1.5 1.5 0 110 3 1.5 1.5 0 010-3z" transform="scale(0.95)"/>', hue: 210 },
  grocery: { glyph: '<path d="M0 0v2h2l3 6.5L3.5 11A2 2 0 005.5 13h9v-2h-9l1-2h6a1 1 0 00.9-.5L15.5 4H4L3 2H0zm5.5 14a1.5 1.5 0 100 3 1.5 1.5 0 000-3zm7 0a1.5 1.5 0 100 3 1.5 1.5 0 000-3z" transform="scale(0.95)"/>', hue: 120 },
  default: { glyph: '<circle cx="8" cy="8" r="4" fill="#fff"/>', hue: 0 },
};

function getCategoryStyle(poiType: string, primaryHue: number): { glyph: string; hue: number } {
  const lower = (poiType || '').toLowerCase();
  for (const [key, val] of Object.entries(CATEGORY_ICONS)) {
    if (key === 'default') continue;
    if (lower.includes(key)) return val;
  }
  return { ...CATEGORY_ICONS.default, hue: (primaryHue + 180) % 360 };
}

function hueToHex(hue: number, sat = 45, light = 35): string {
  const h = hue / 360, s = sat / 100, l = light / 100;
  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1; if (t > 1) t -= 1;
    if (t < 1/6) return p + (q-p)*6*t;
    if (t < 1/2) return q;
    if (t < 2/3) return p + (q-p)*(2/3-t)*6;
    return p;
  };
  const q = l < 0.5 ? l*(1+s) : l+s-l*s;
  const p = 2*l - q;
  const r = Math.round(hue2rgb(p,q,h+1/3)*255);
  const g = Math.round(hue2rgb(p,q,h)*255);
  const b = Math.round(hue2rgb(p,q,h-1/3)*255);
  return `#${((1<<24)+(r<<16)+(g<<8)+b).toString(16).slice(1)}`;
}

function hexToHue(hex: string): number {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!m) return 200;
  const r = parseInt(m[1],16)/255, g = parseInt(m[2],16)/255, b = parseInt(m[3],16)/255;
  const max = Math.max(r,g,b), min = Math.min(r,g,b);
  if (max === min) return 0;
  const d = max-min;
  let h = 0;
  if (max === r) h = ((g-b)/d + (g<b?6:0))/6;
  else if (max === g) h = ((b-r)/d+2)/6;
  else h = ((r-g)/d+4)/6;
  return Math.round(h*360);
}

function buildMapStyles(primaryColor: string) {
  const hue = hexToHue(primaryColor);
  return [
    { elementType: 'geometry', stylers: [{ color: '#f5f5f5' }] },
    { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
    { elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
    { elementType: 'labels.text.stroke', stylers: [{ color: '#f5f5f5' }] },
    { featureType: 'administrative.land_parcel', elementType: 'labels.text.fill', stylers: [{ color: '#bdbdbd' }] },
    { featureType: 'poi', elementType: 'geometry', stylers: [{ color: '#eeeeee' }] },
    { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
    { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#e5e5e5' }] },
    { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
    { featureType: 'road.arterial', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
    { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#dadada' }] },
    { featureType: 'road.highway', elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
    { featureType: 'transit.line', elementType: 'geometry', stylers: [{ color: '#e5e5e5' }] },
    { featureType: 'water', elementType: 'geometry', stylers: [{ color: hueToHex(hue, 15, 85) }] },
    { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] },
  ];
}

// Popup shown when a POI marker (or its list link) is selected. Built as DOM
// nodes so text is set via textContent (no HTML-injection surface). Mirrors the
// reference's marker popup: name, address, "Get Directions" + "Visit Website".
function buildInfoContent(poi: POI, primaryColor: string): HTMLElement {
  const wrap = document.createElement('div');
  wrap.style.cssText = 'font-family:var(--vibe-font-body,sans-serif);min-width:150px;max-width:230px;padding:2px 2px 4px;';

  const name = document.createElement('div');
  name.textContent = poi.poi_name || '';
  name.style.cssText = 'font-weight:600;font-size:14px;color:#111;line-height:1.3;';
  wrap.appendChild(name);

  if (hasVal(poi.poi_address)) {
    const addr = document.createElement('div');
    addr.textContent = poi.poi_address;
    addr.style.cssText = 'font-size:12px;color:#666;margin-top:3px;line-height:1.35;';
    wrap.appendChild(addr);
  }

  const dir = hasVal(poi.poi_directions_map_url) ? poi.poi_directions_map_url : '';
  const web = hasVal(poi.poi_business_url) ? poi.poi_business_url : '';
  if (dir || web) {
    const actions = document.createElement('div');
    actions.style.cssText = 'display:flex;gap:6px;margin-top:8px;flex-wrap:wrap;';
    if (dir) {
      const a = document.createElement('a');
      a.href = dir; a.target = '_blank'; a.rel = 'noopener noreferrer';
      a.textContent = 'Get Directions';
      a.style.cssText = `font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:0.04em;padding:6px 12px;border-radius:4px;text-decoration:none;background:${primaryColor};color:#fff;`;
      actions.appendChild(a);
    }
    if (web) {
      const a = document.createElement('a');
      a.href = web; a.target = '_blank'; a.rel = 'noopener noreferrer';
      a.textContent = 'Visit Website';
      a.style.cssText = `font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:0.04em;padding:6px 12px;border-radius:4px;text-decoration:none;border:1px solid ${primaryColor};color:#111;`;
      actions.appendChild(a);
    }
    wrap.appendChild(actions);
  }
  return wrap;
}

// ============================================================================
// Google Map component
// ============================================================================

function GoogleMap({
  propertyLat, propertyLng, propertyIcon, primaryColor, pois, activeTab,
  selectedPoiIndex, onMarkerClick,
}: {
  propertyLat: number;
  propertyLng: number;
  propertyIcon: string;
  primaryColor: string;
  pois: POI[];
  activeTab: string;
  selectedPoiIndex: number | null;
  onMarkerClick: (index: number) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const markersRef = useRef<{ marker: any; normalIcon: any; highlightIcon: any; poiIndex: number }[]>([]);
  const infoWindowRef = useRef<any>(null);
  const [ready, setReady] = useState(false);
  const primaryHue = useMemo(() => hexToHue(primaryColor), [primaryColor]);

  useEffect(() => {
    let cancelled = false;
    loadGoogleMaps().then(() => {
      if (cancelled || !containerRef.current) return;
      const g = (window as any).google.maps;
      const map = new g.Map(containerRef.current, {
        center: { lat: propertyLat, lng: propertyLng },
        zoom: 14,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        zoomControl: true,
        zoomControlOptions: { position: g.ControlPosition.RIGHT_BOTTOM },
        styles: buildMapStyles(primaryColor),
      });

      infoWindowRef.current = new g.InfoWindow();

      const hasIcon = !!propertyIcon;
      new g.Marker({
        position: { lat: propertyLat, lng: propertyLng },
        map, title: 'Property', zIndex: 999,
        icon: {
          url: makePropertyPinBg(primaryColor, hasIcon),
          scaledSize: new g.Size(60, 74),
          anchor: new g.Point(30, 74),
        },
      });
      if (hasIcon) {
        new g.Marker({
          position: { lat: propertyLat, lng: propertyLng },
          map, zIndex: 1000, clickable: false,
          icon: {
            url: propertyIcon,
            scaledSize: new g.Size(24, 24),
            anchor: new g.Point(12, 59),
          },
        });
      }
      mapRef.current = map;
      setReady(true);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [propertyLat, propertyLng, primaryColor, propertyIcon]);

  const syncMarkers = useCallback(() => {
    const map = mapRef.current;
    const g = (window as any).google?.maps;
    if (!map || !g) return;

    markersRef.current.forEach((m) => m.marker.setMap(null));
    markersRef.current = [];

    const visible = activeTab === 'all'
      ? pois.map((p, i) => ({ poi: p, origIndex: i }))
      : pois.map((p, i) => ({ poi: p, origIndex: i })).filter(({ poi }) => (poi.poi_type || 'Nearby') === activeTab);

    const bounds = new g.LatLngBounds();
    bounds.extend({ lat: propertyLat, lng: propertyLng });
    let hasPoiBounds = false;
    const iconCache = new Map<string, { normal: any; highlight: any }>();

    for (const { poi, origIndex } of visible) {
      const lat = parseFloat(poi.poi_latitude || '');
      const lng = parseFloat(poi.poi_longitude || '');
      if (isNaN(lat) || isNaN(lng)) continue;

      const pos = { lat, lng };
      bounds.extend(pos);
      hasPoiBounds = true;

      const cat = poi.poi_type || 'Nearby';
      if (!iconCache.has(cat)) {
        const style = getCategoryStyle(cat, primaryHue);
        iconCache.set(cat, {
          normal: { url: makeSvgMarker('#111111', style.glyph, 28), scaledSize: new g.Size(28, 36), anchor: new g.Point(14, 36) },
          highlight: { url: makeHighlightMarker(primaryColor, style.glyph, 40), scaledSize: new g.Size(40, 54), anchor: new g.Point(20, 54) },
        });
      }

      const icons = iconCache.get(cat)!;
      const isSelected = selectedPoiIndex === origIndex;

      const marker = new g.Marker({
        position: pos, map,
        title: poi.poi_name || '',
        icon: isSelected ? icons.highlight : icons.normal,
        zIndex: isSelected ? 900 : 1,
        animation: isSelected ? g.Animation.BOUNCE : null,
      });

      if (isSelected) setTimeout(() => marker.setAnimation(null), 1400);

      marker.addListener('click', () => onMarkerClick(origIndex));

      markersRef.current.push({ marker, normalIcon: icons.normal, highlightIcon: icons.highlight, poiIndex: origIndex });
    }

    if (hasPoiBounds) {
      map.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
      const listener = g.event.addListener(map, 'idle', () => {
        if (map.getZoom() > 15) map.setZoom(15);
        g.event.removeListener(listener);
      });
    }
  }, [pois, activeTab, propertyLat, propertyLng, primaryHue, primaryColor, selectedPoiIndex, onMarkerClick]);

  useEffect(() => { if (ready) syncMarkers(); }, [ready, syncMarkers]);

  // When selected POI changes, update marker icons, pan, and open a popup.
  useEffect(() => {
    const g = (window as any).google?.maps;
    const map = mapRef.current;
    const iw = infoWindowRef.current;
    if (!g || !map || !ready) return;

    let selected: { marker: any; highlightIcon: any; normalIcon: any; poiIndex: number } | null = null;
    for (const entry of markersRef.current) {
      const isSelected = entry.poiIndex === selectedPoiIndex;
      entry.marker.setIcon(isSelected ? entry.highlightIcon : entry.normalIcon);
      entry.marker.setZIndex(isSelected ? 900 : 1);
      if (isSelected) selected = entry;
    }

    if (selected && selectedPoiIndex !== null) {
      const marker = selected.marker;
      marker.setAnimation(g.Animation.BOUNCE);
      setTimeout(() => marker.setAnimation(null), 1400);
      map.panTo(marker.getPosition());
      const poi = pois[selectedPoiIndex];
      if (iw && poi) {
        iw.setContent(buildInfoContent(poi, primaryColor));
        iw.open(map, marker);
      }
    } else if (iw) {
      iw.close();
    }
  }, [selectedPoiIndex, ready, pois, primaryColor]);

  return (
    <div className="w-full h-full relative rounded overflow-hidden">
      <div ref={containerRef} className="w-full h-full" />
      {!ready && (
        <div className="absolute inset-0 flex items-center justify-center bg-[#f5f5f5]">
          <span className="font-body text-sm text-[var(--vibe-color-text-muted)] animate-pulse">Loading map…</span>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Main component
// ============================================================================

export function NeighborhoodMap() {
  const property = useProperty();
  const cta = useSlotGroup('neighborhoodmap.cta', ctaDef);
  const pois = useCollectionBinding<POI>('neighborhoodmap.pois', { collection: 'neighborhood_pois' });

  const propLat = usePropertyField('property_address_latitude');
  const propLng = usePropertyField('property_address_longitude');
  const propertyIcon = (property?.property_icon as string) || '';
  const primaryColor = (property?.property_primary_color as string) || '#4a7c59';

  const allPois = pois && pois.length > 0 ? pois : [];

  const categories = useMemo(() => {
    const cats = new Set<string>();
    for (const poi of allPois) cats.add(poi.poi_type || 'Nearby');
    return Array.from(cats);
  }, [allPois]);

  const [activeTab, setActiveTab] = useState('all');
  const [selectedPoiIndex, setSelectedPoiIndex] = useState<number | null>(null);
  const [expanded, setExpanded] = useState(false);
  const mapRef = useRef<HTMLDivElement>(null);

  // Show a compact slice of long lists by default (esp. the "All" tab), with
  // "Show all" revealing the rest. Desktop shows 4 rows of the grid (16); on
  // tablet the grid drops to 2 columns, so cap at 2 × 4 = 8 to keep the same
  // tidy 4-row block. On mobile the grid is a single column, so cap at 5 rows
  // to keep the default list short with "Show all" sitting just below it.
  const [poiLimit, setPoiLimit] = useState(16);
  useEffect(() => {
    const mobileMq = window.matchMedia('(max-width: 767px)');
    const tabletMq = window.matchMedia('(min-width: 768px) and (max-width: 1023px)');
    const update = () => setPoiLimit(mobileMq.matches ? 5 : tabletMq.matches ? 8 : 16);
    update();
    mobileMq.addEventListener('change', update);
    tabletMq.addEventListener('change', update);
    return () => {
      mobileMq.removeEventListener('change', update);
      tabletMq.removeEventListener('change', update);
    };
  }, []);

  const handleMarkerClick = useCallback((origIndex: number) => {
    setSelectedPoiIndex(origIndex);
  }, []);

  const handleTabChange = useCallback((tab: string) => {
    setActiveTab(tab);
    setSelectedPoiIndex(null);
    setExpanded(false);
  }, []);

  // A POI link may sit in a category the map is currently filtered away from;
  // reset to "all" so its marker is visible, then select + scroll to the map.
  const handlePoiLink = useCallback((poi: POI, origIndex: number) => {
    const cat = poi.poi_type || 'Nearby';
    if (activeTab !== 'all' && activeTab !== cat) setActiveTab('all');
    setSelectedPoiIndex(origIndex);
    mapRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [activeTab]);

  const lat = parseFloat(propLat as string || '');
  const lng = parseFloat(propLng as string || '');
  const hasCoords = !isNaN(lat) && !isNaN(lng);

  const allTabs = [{ key: 'all', label: 'All' }, ...categories.map((c) => ({ key: c, label: c }))];
  const poisFor = (key: string) => {
    const items = allPois
      .map((poi, origIndex) => ({ poi, origIndex }))
      .filter(({ poi }) => key === 'all' || (poi.poi_type || 'Nearby') === key);
    // The "All" view aggregates every category, so a place listed under more
    // than one category would otherwise appear multiple times — collapse those
    // to a single entry (keeping the first occurrence). Per-category tabs never
    // repeat a place, so they're left untouched.
    if (key !== 'all') return items;
    const seen = new Set<string>();
    return items.filter(({ poi }) => {
      const id = poiIdentity(poi);
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  };

  return (
    <section className="py-8 md:py-12 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8 md:mb-10">
          <RichTextContent
            path="neighborhoodmap.heading"
            label="Heading"
            defaultValue="<p>Everything Is Within Reach</p>"
            as="h2"
            className="font-heading text-[clamp(1.2rem,5.8vw,2.5rem)] whitespace-nowrap sm:whitespace-normal font-semibold uppercase tracking-wide text-[var(--vibe-color-primary)] leading-[1.1] mb-4"
          />
          <div className="max-w-3xl mx-auto">
            <RichTextContent
              path="neighborhoodmap.body"
              label="Body Text"
              defaultValue='<p>Live at <span data-type="field-token" data-field="property_name"></span> in <span data-type="field-token" data-field="property_address_city"></span>, <span data-type="field-token" data-field="property_address_state"></span> with everything you need just minutes away.</p>'
              as="div"
              className="font-body text-base leading-relaxed text-[var(--vibe-color-text-muted)] [&_p]:mb-4 [&_a]:text-primary [&_a]:underline"
            />
          </div>

          <div className="mt-6 flex justify-center">
            <EditableSlotGroup groupPath="neighborhoodmap.cta" definition={ctaDef}>
              <a
                href={cta.url || '/schedule-a-tour'}
                target={cta._target || '_self'}
                className="nbm-cta inline-flex items-center justify-center whitespace-nowrap px-6 py-3 font-heading text-sm md:px-4 md:tracking-[0.1em] lg:px-12 lg:py-4 lg:text-base lg:tracking-[0.15em] uppercase tracking-[0.15em] transition-all"
              >
                {cta.label || 'Schedule a Tour'}
              </a>
            </EditableSlotGroup>
          </div>
        </div>

      </div>

      {/* Full-width map on top — matches the reference (map, then tabs, then
          columns of POI links below). */}
      <div ref={mapRef} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="nbm-map">
          {hasCoords ? (
            <GoogleMap
              propertyLat={lat} propertyLng={lng}
              propertyIcon={propertyIcon} primaryColor={primaryColor}
              pois={allPois} activeTab={activeTab}
              selectedPoiIndex={selectedPoiIndex}
              onMarkerClick={handleMarkerClick}
            />
          ) : (
            <div className="w-full h-full bg-[#f5f5f5] flex items-center justify-center text-[var(--vibe-color-text-muted)]">
              <p className="font-body">Map coordinates not available</p>
            </div>
          )}
        </div>
      </div>

      {categories.length > 0 && (() => {
        // Order longest names first so the two-line entries cluster in the top
        // rows and the tidy single-line ones fill in below. Length is a
        // deterministic proxy for "wraps to two lines" — unlike DOM measurement
        // it stays identical on server + client, so there's no hydration reflow.
        // (Array.sort is stable, so equal-length names keep their source order.)
        const activeItems = [...poisFor(activeTab)].sort(
          (a, b) => (b.poi.poi_name?.length || 0) - (a.poi.poi_name?.length || 0),
        );
        const visibleItems = expanded ? activeItems : activeItems.slice(0, poiLimit);
        const hiddenCount = activeItems.length - visibleItems.length;
        return (
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-8 md:mt-10">
            {/* Category filter pills — size to their labels (no truncation),
                wrap to as many rows as needed, each with a POI count. */}
            <div className="nbm-tabrow" role="tablist" aria-label="Points of interest categories">
              {allTabs.map((tab) => {
                const count = poisFor(tab.key).length;
                return (
                  <button
                    key={tab.key}
                    role="tab"
                    aria-selected={activeTab === tab.key}
                    className={`nbm-tab${activeTab === tab.key ? ' nbm-tab--active' : ''}`}
                    onClick={() => handleTabChange(tab.key)}
                  >
                    <span className="nbm-tab-label">{tab.label}</span>
                    <span className="nbm-tab-count">{count}</span>
                  </button>
                );
              })}
            </div>

            {/* Only the active category renders — as a responsive grid that
                fills the available width. Names wrap to two lines instead of
                being clipped, so full business names stay readable. */}
            {activeItems.length > 0 ? (
              <>
                <ul className="nbm-grid">
                  {visibleItems.map(({ poi, origIndex }) => (
                    <li key={origIndex}>
                      <button
                        className={`nbm-poi-link${selectedPoiIndex === origIndex ? ' nbm-poi-link--active' : ''}`}
                        onClick={() => handlePoiLink(poi, origIndex)}
                      >
                        {(() => {
                          const words = (poi.poi_name || '').trim().split(/\s+/);
                          const last = words.pop() || '';
                          const head = words.join(' ');
                          return (
                            <span className="nbm-poi-name">
                              {head ? head + ' ' : ''}
                              {/* Last word + arrow stay on one line so the arrow
                                  never wraps onto a line by itself. */}
                              <span className="nbm-poi-tail">
                                {last}
                                <svg className="nbm-arrow" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                                  <path d="M4.5 9.5l5-5M5 4.5h4.5V9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </span>
                            </span>
                          );
                        })()}
                      </button>
                    </li>
                  ))}
                </ul>
                {activeItems.length > poiLimit && (
                  <div className="nbm-more-wrap">
                    <button className="nbm-more" onClick={() => setExpanded((v) => !v)}>
                      {expanded ? 'Show fewer' : `Show all ${activeItems.length}`}
                      {!expanded && hiddenCount > 0 ? ` (+${hiddenCount})` : ''}
                    </button>
                  </div>
                )}
              </>
            ) : (
              <p className="nbm-empty">No locations in this category.</p>
            )}
          </div>
        );
      })()}

      <style>{`
        /* ===== Section CTA (solid primary pill) ===== */
        .nbm-cta {
          background: var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary, #fff);
          border: 1.5px solid var(--vibe-color-primary);
          border-radius: 9999px;
          text-decoration: none;
        }
        .nbm-cta:hover {
          background: color-mix(in srgb, var(--vibe-color-primary) 88%, #000);
          border-color: color-mix(in srgb, var(--vibe-color-primary) 88%, #000);
          transform: translateY(-1px);
          box-shadow: 0 6px 18px color-mix(in srgb, var(--vibe-color-primary) 30%, transparent);
        }

        /* ===== Map (full-width, prominent — sits on top) ===== */
        .nbm-map {
          width: 100%;
          height: 380px;
          border-radius: 8px;
          overflow: hidden;
          position: relative;
        }
        @media (min-width: 768px) { .nbm-map { height: 480px; } }
        @media (min-width: 1024px) { .nbm-map { height: 540px; } }

        /* ===== Category filter pills (wrap, size to label — no truncation) ===== */
        .nbm-tabrow {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 0.5rem;
        }
        .nbm-tab {
          appearance: none;
          display: inline-flex;
          align-items: center;
          gap: 0.45rem;
          background: color-mix(in srgb, var(--vibe-color-text-muted) 8%, transparent);
          border: 1px solid var(--vibe-color-border);
          border-radius: 9999px;
          padding: 0.5rem 0.95rem;
          font-family: var(--vibe-font-heading, var(--vibe-font-body));
          font-size: 0.8rem;
          font-weight: 600;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          color: var(--vibe-color-text-muted);
          cursor: pointer;
          white-space: nowrap;
          transition: color 0.2s, background 0.2s, border-color 0.2s;
        }
        @media (min-width: 768px) { .nbm-tab { font-size: 0.85rem; } }
        .nbm-tab:hover {
          color: var(--vibe-color-primary);
          border-color: var(--vibe-color-primary);
          background: color-mix(in srgb, var(--vibe-color-primary) 8%, transparent);
        }
        /* Active pill: solid, slightly-darkened primary with explicit white
           text. The theme's auto-computed --vibe-color-text-on-primary flips to
           dark on lighter primaries (e.g. this gold), which reads low-contrast
           on the pill — darkening the fill keeps white legible everywhere. */
        .nbm-tab--active,
        .nbm-tab--active:hover {
          background: color-mix(in srgb, var(--vibe-color-primary) 90%, #000);
          border-color: color-mix(in srgb, var(--vibe-color-primary) 90%, #000);
          color: #fff;
        }
        .nbm-tab-count {
          font-size: 0.72rem;
          font-weight: 700;
          line-height: 1;
          padding: 0.15em 0.5em;
          border-radius: 9999px;
          background: color-mix(in srgb, currentColor 15%, transparent);
        }

        /* ===== Active-category POI grid (fills width; names wrap 2 lines) ===== */
        .nbm-grid {
          list-style: none;
          margin: 1.75rem 0 0;
          padding: 0;
          display: grid;
          /* Responsive, but never more than 4 columns across: each column is at
             least 230px, and at least a quarter of the row (minus the 3 gaps). */
          grid-template-columns: repeat(auto-fill, minmax(max(230px, calc((100% - 3 * 1.75rem) / 4)), 1fr));
          gap: 0.15rem 1.75rem;
        }
        /* Tablet: pin to exactly 2 columns so the default slice (8 POIs) lays
           out as a tidy 2 × 4 block, with "Show all" sitting below it. */
        @media (min-width: 768px) and (max-width: 1023px) {
          .nbm-grid { grid-template-columns: repeat(2, 1fr); }
        }
        .nbm-poi-link {
          display: block;
          width: 100%;
          padding: 0.45rem 0;
          background: transparent;
          border: none;
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          line-height: 1.35;
          text-align: center;
          color: var(--vibe-color-text);
          cursor: pointer;
          transition: color 0.15s;
        }
        .nbm-poi-link:hover,
        .nbm-poi-link--active { color: var(--vibe-color-primary); }
        /* Arrow lives inline at the end of the name, so it always trails the
           last word with a fixed gap — regardless of one- or two-line names. */
        .nbm-poi-name {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .nbm-poi-tail { white-space: nowrap; }
        .nbm-arrow {
          display: inline-block;
          vertical-align: middle;
          margin-left: 0.35rem;
          color: currentColor;
          transition: transform 0.15s;
        }
        .nbm-poi-link:hover .nbm-arrow { transform: translate(2px, -2px); }

        /* ===== "Show all" expander + empty state ===== */
        .nbm-more-wrap { display: flex; justify-content: center; margin-top: 1.5rem; padding-bottom: 20px; }
        .nbm-more {
          appearance: none;
          background: transparent;
          border: 1px solid var(--vibe-color-border);
          border-radius: 9999px;
          padding: 0.55rem 1.5rem;
          font-family: var(--vibe-font-heading, var(--vibe-font-body));
          font-size: 0.8rem;
          font-weight: 600;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: var(--vibe-color-primary);
          cursor: pointer;
          transition: background 0.2s, border-color 0.2s;
        }
        .nbm-more:hover {
          background: color-mix(in srgb, var(--vibe-color-primary) 10%, transparent);
          border-color: var(--vibe-color-primary);
        }
        .nbm-empty {
          margin-top: 1.75rem;
          text-align: center;
          font-family: var(--vibe-font-body);
          font-size: 0.9rem;
          color: var(--vibe-color-text-muted);
        }

        /* ===== Mobile: shrink the category pills + POI list a touch. POI
           links are already center-aligned at every breakpoint (see the base
           .nbm-poi-link rule above). ===== */
        @media (max-width: 767px) {
          .nbm-tab {
            font-size: 0.72rem;
            padding: 0.4rem 0.8rem;
          }
          .nbm-tab-count { font-size: 0.66rem; }
          .nbm-poi-link { font-size: 0.9rem; }
          /* At single-column the full row width is available, so drop the
             2-line clamp to let long names breathe onto a third line if needed. */
          .nbm-poi-name {
            display: block;
            overflow: visible;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .nbm-poi-link, .nbm-arrow { transition: none; }
        }

        /* ===== Google Maps info window cleanup ===== */
        .gm-style .gm-style-iw-c { border-radius: 6px !important; box-shadow: 0 4px 20px rgba(0,0,0,0.12) !important; padding: 8px 12px !important; }
        .gm-style .gm-style-iw-d { overflow: hidden !important; }
        .gm-style .gm-style-iw-tc::after { background: white !important; }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'NeighborhoodMap',
  displayName: 'Neighborhood Map',
  tokens: {
    'neighborhoodmap.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue: '<p>Everything Is Within Reach</p>',
    },
    'neighborhoodmap.body': {
      label: 'Body Text',
      type: 'richtext',
      defaultValue:
        '<p>Live at <span data-type="field-token" data-field="property_name"></span> in <span data-type="field-token" data-field="property_address_city"></span>, <span data-type="field-token" data-field="property_address_state"></span> with everything you need just minutes away.</p>',
    },
  },
  slotGroups: {
    'neighborhoodmap.cta': {
      label: 'CTA',
      fields: {
        url: { label: 'Button URL', type: 'url', defaultValue: '/schedule-a-tour' },
        label: { label: 'Button Label', type: 'text', defaultValue: 'Schedule a Tour' },
        _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
      },
    },
  },
  collectionBindings: {
    'neighborhoodmap.pois': {
      label: 'Points of Interest',
      collection: 'neighborhood_pois',
    },
  },
};

(NeighborhoodMap as any).contentSchema = contentSchema;

export default NeighborhoodMap;
